import React, { useCallback, useEffect } from 'react';
import { Menu, Input, MenuItem, Icon, Table, Dropdown } from 'semantic-ui-react';
import classNames from 'classnames';
import api from '../../api';
import Config from '../../constants/Config';
import styles from './Payment.module.scss';
import { useField } from '../../hooks';
import { PayTypesMap, PayTypes } from '../../constants/Enums';
import EditPayPopup from '../EditPayPopup';
import PayActionsPopup from '../PayActionsPopup';
import moment from 'moment';
import { calculateEffectiveYears } from '../../utils/calculate-effective-years';

const paymentMethodMap = new Map([
  ['alipay', '支付宝'],
  ['wechat', '微信'],
  ['workWechat', '企业微信'],
]);
const paymentMethodOptions = [
  {
    key: '',
    text: '全部',
    value: '',
  },
  {
    key: 'alipay',
    text: '支付宝',
    value: 'alipay',
  },
  {
    key: 'wechat',
    text: '微信',
    value: 'wechat',
  },
  {
    key: 'workWechat',
    text: '企业微信',
    value: 'workWechat',
  },
];

const payStatusOptions = [
  {
    key: '',
    text: '全部',
    value: '',
  },
  ...Array.from(PayTypesMap, ([key, text]) => ({
    key,
    text,
    value: key,
  })),
];
const settlementManage = React.memo(() => {
  const [sortable, setSortable] = React.useState({
    column: null,
    direction: null,
  });
  const [value, handleFieldChange] = useField('');
  const [paymentMethod, setPaymentMethod] = React.useState('');
  const [paymentStatus, setPaymentStatus] = React.useState('');
  const [currentSkip, setCurrrentSkip] = React.useState(1);
  const [listObj, setListObj] = React.useState({
    list: [],
    total: 0,
  });

  const handleChange = useCallback(
    (_, { value: selectedMethod }) => {
      setCurrrentSkip(1);
      setPaymentMethod(selectedMethod || '');
    },
    [setPaymentMethod],
  );

  const handlePayStatusChange = useCallback(
    (_, { value: selectedStatus }) => {
      setCurrrentSkip(1);
      setPaymentStatus(selectedStatus || '');
    },
    [setPaymentStatus],
  );

  const init = React.useCallback(() => {
    api
      .getPaymentLists({
        limit: Config.AONNOUCEMENT_LIMIT,
        skip: currentSkip - 1,
        paymentMethod,
        paymentStatus,
        sortable: sortable.column
          ? `${sortable.column} ${sortable.direction === 'ascending' ? 'asc' : 'desc'}`
          : '',
        search: value,
      })
      .then((res) => {
        setListObj({ list: res.item, total: res.total });
      });
  }, [currentSkip, paymentMethod, paymentStatus, sortable.column, sortable.direction, value]);

  const again = () => {
    setCurrrentSkip(1);
    setListObj({ list: [], total: 0 });
    init();
  };
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      again();
    }
  };
  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSkip, paymentMethod, paymentStatus, sortable]);
  return (
    <div className={styles.app}>
      <div className={styles.table}>
        <div className={styles.box}>
          <Dropdown
            value={paymentMethod}
            clearable
            placeholder="付款方式"
            selection
            options={paymentMethodOptions}
            onChange={handleChange}
          />
          <Dropdown
            value={paymentStatus}
            clearable
            placeholder="支付状态"
            selection
            options={payStatusOptions}
            onChange={handlePayStatusChange}
          />
          <Input
            value={value}
            onChange={handleFieldChange}
            action={{
              content: '查找',
              onClick: again,
            }}
            onKeyPress={handleKeyPress}
            placeholder="订单编号/付款用户"
            className={styles.input}
          />
        </div>
        <Table celled unstackable fixed sortable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={4}>订单编号</Table.HeaderCell>
              <Table.HeaderCell width={2}>付款用户</Table.HeaderCell>
              <Table.HeaderCell
                width={2}
                sorted={sortable.column === 'numberOfPeople' ? sortable.direction : null}
                onClick={() => {
                  let direction = 'ascending';
                  if (sortable.column === 'numberOfPeople') {
                    direction = sortable.direction === 'ascending' ? 'descending' : 'ascending';
                  }
                  setSortable({
                    column: 'numberOfPeople',
                    direction,
                  });
                }}
              >
                人数
                {sortable.column !== 'numberOfPeople' && <Icon name="sort" />}
              </Table.HeaderCell>
              <Table.HeaderCell width={1}>年限</Table.HeaderCell>
              <Table.HeaderCell
                width={2}
                sorted={sortable.column === 'paymentAmount' ? sortable.direction : null}
                onClick={() => {
                  let direction = 'ascending';
                  if (sortable.column === 'paymentAmount') {
                    direction = sortable.direction === 'ascending' ? 'descending' : 'ascending';
                  }
                  setSortable({
                    column: 'paymentAmount',
                    direction,
                  });
                }}
              >
                订单金额
                {sortable.column !== 'paymentAmount' && <Icon name="sort" />}
              </Table.HeaderCell>
              <Table.HeaderCell width={1}>优惠金额</Table.HeaderCell>
              <Table.HeaderCell
                width={2}
                sorted={
                  sortable.column === 'promotionAmountAfterDiscount' ? sortable.direction : null
                }
                onClick={() => {
                  let direction = 'ascending';
                  if (sortable.column === 'promotionAmountAfterDiscount') {
                    direction = sortable.direction === 'ascending' ? 'descending' : 'ascending';
                  }
                  setSortable({
                    column: 'promotionAmountAfterDiscount',
                    direction,
                  });
                }}
              >
                付款金额
                {sortable.column !== 'promotionAmountAfterDiscount' && <Icon name="sort" />}
              </Table.HeaderCell>
              <Table.HeaderCell width={1}>付款方式</Table.HeaderCell>
              <Table.HeaderCell width={2}>状态</Table.HeaderCell>
              <Table.HeaderCell width={2}>创建时间</Table.HeaderCell>
              <Table.HeaderCell
                width={2}
                sorted={sortable.column === 'createdAt' ? sortable.direction : null}
                onClick={() => {
                  let direction = 'ascending';
                  if (sortable.column === 'createdAt') {
                    direction = sortable.direction === 'ascending' ? 'descending' : 'ascending';
                  }
                  setSortable({
                    column: 'createdAt',
                    direction,
                  });
                }}
              >
                支付时间
                {sortable.column !== 'createdAt' && <Icon name="sort" />}
              </Table.HeaderCell>
              <Table.HeaderCell width={2}>有效期</Table.HeaderCell>
              <Table.HeaderCell width={2}>原有效期</Table.HeaderCell>
              <Table.HeaderCell width={2}>操作记录</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {!!listObj.list.length &&
              listObj.list.map((item) => (
                <Table.Row
                  key={item.id}
                  style={{ color: item.status === PayTypes.EXPIRED ? '#aaa' : '' }}
                >
                  <Table.Cell>{item.id}</Table.Cell>
                  <Table.Cell>
                    {item.creatorUserId && (item.creatorUser.username || item.creatorUser.email)}
                  </Table.Cell>
                  <Table.Cell>{item.buyNumber || '-'}</Table.Cell>
                  <Table.Cell>{calculateEffectiveYears(item)}</Table.Cell>
                  <Table.Cell>{item.paymentAmount}</Table.Cell>
                  <Table.Cell>
                    {item.promotionUserId
                      ? (item.paymentAmount - item.promotionAmountAfterDiscount).toFixed(2)
                      : 0}
                  </Table.Cell>
                  <Table.Cell>
                    {item.promotionAmountAfterDiscount}
                    {item.status === PayTypes.UNPAID && (
                      <EditPayPopup id={item.id} onUpdate={again}>
                        <Icon
                          name="edit outline"
                          style={{ marginLeft: '10px', color: '#1890ff', cursor: 'pointer' }}
                        />
                      </EditPayPopup>
                    )}
                  </Table.Cell>
                  <Table.Cell>{paymentMethodMap.get(item.paymentMethod)}</Table.Cell>
                  <Table.Cell>{PayTypesMap.get(item.status)}</Table.Cell>
                  <Table.Cell>{item.createdAt && moment(item.createdAt).format('')}</Table.Cell>
                  <Table.Cell>{item.paymentAt && moment(item.paymentAt).format('')}</Table.Cell>
                  <Table.Cell>{item.expiredAt && moment(item.expiredAt).format('')}</Table.Cell>
                  <Table.Cell>{item.originalAt && moment(item.originalAt).format('')}</Table.Cell>
                  <Table.Cell>
                    <PayActionsPopup id={item.id}>
                      <button type="button">记录</button>
                    </PayActionsPopup>
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell colSpan="14">
                <Menu floated="right" pagination>
                  <MenuItem
                    as="a"
                    icon
                    onClick={() => {
                      setCurrrentSkip((pre) => {
                        if (pre <= 1) {
                          return 1;
                        }
                        return pre - 1;
                      });
                    }}
                  >
                    <Icon name="chevron left" />
                  </MenuItem>
                  {Array.from({ length: listObj.total }, (_, index) => (
                    <MenuItem
                      as="a"
                      key={index}
                      className={classNames(index + 1 === currentSkip && styles.menuItemActive)}
                      onClick={() => {
                        setCurrrentSkip(index + 1);
                      }}
                    >
                      {index + 1}
                    </MenuItem>
                  ))}
                  <MenuItem
                    as="a"
                    icon
                    onClick={() => {
                      setCurrrentSkip((pre) => {
                        if (pre + 1 >= listObj.total) {
                          return listObj.total;
                        }
                        return pre + 1;
                      });
                    }}
                  >
                    <Icon name="chevron right" />
                  </MenuItem>
                </Menu>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
      </div>
    </div>
  );
});

export default settlementManage;
